import { trackAddToCart } from "@/lib/analytics";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Package, X, ShoppingCart, Check, Disc3, ChevronLeft, ChevronRight,
  Sparkles, Tag, Loader2, Share2, Copy, CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format price helper
const formatPrice = (price) => {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(price || 0);
};

// Pack Card Component
const PackCard = ({ pack, onViewDetails, onAddToCart, adding }) => {
  const primaryImage = pack.images?.find(img => img.is_primary)?.url || pack.images?.[0]?.url;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group relative bg-gradient-to-br from-zinc-900 to-black border border-[#D4AF37]/20 rounded-lg overflow-hidden hover:border-[#D4AF37]/50 transition-all duration-300"
      data-testid={`pack-card-${pack.id}`}
    >
      {/* Savings Badge */}
      {pack.savings > 0 && (
        <div className="absolute top-3 left-3 z-10 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">
          Save {formatPrice(pack.savings)}
        </div>
      )}
      
      {/* Featured Badge */}
      {pack.is_featured && (
        <div className="absolute top-3 right-3 z-10 bg-[#D4AF37] text-black text-xs font-bold px-2 py-1 rounded flex items-center gap-1">
          <Sparkles className="h-3 w-3" /> Featured
        </div>
      )}
      
      {/* Image */}
      <div 
        className="aspect-square bg-zinc-900 relative cursor-pointer overflow-hidden"
        onClick={() => onViewDetails(pack)}
      >
        {primaryImage ? (
          <img 
            src={primaryImage} 
            alt={pack.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#D4AF37]/10 to-zinc-900">
            <Package className="h-16 w-16 text-[#D4AF37]/30" />
          </div>
        )}
        
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Button 
            className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
            onClick={(e) => { e.stopPropagation(); onViewDetails(pack); }}
          >
            View Pack Details
          </Button>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <h3 
              className="text-lg text-white font-bold tracking-wide cursor-pointer hover:text-[#D4AF37] transition-colors"
              style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}
              onClick={() => onViewDetails(pack)}
            >
              {pack.name}
            </h3>
            <p className="text-xs text-zinc-500">{pack.sku}</p>
          </div>
        </div>
        
        {pack.short_description && (
          <p className="text-sm text-zinc-400 mb-3 line-clamp-2">{pack.short_description}</p>
        )}
        
        {/* Cymbals Preview */}
        <div className="flex flex-wrap gap-1 mb-3">
          {pack.cymbals?.slice(0, 3).map((cymbal, idx) => (
            <span key={idx} className="text-xs bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded">
              {cymbal.size} {cymbal.type}
            </span>
          ))}
          {pack.cymbals?.length > 3 && (
            <span className="text-xs text-zinc-500">+{pack.cymbals.length - 3} more</span>
          )}
        </div>
        
        {/* Pricing */}
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xs text-zinc-500 line-through">{formatPrice(pack.rrp_total)} RRP</p>
            <p className="text-2xl font-bold text-[#D4AF37]">{formatPrice(pack.pack_price)}</p>
          </div>
          <Button
            size="sm"
            onClick={() => onAddToCart(pack)}
            disabled={adding === pack.id}
            className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
            data-testid={`add-pack-${pack.id}`}
          >
            {adding === pack.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <ShoppingCart className="h-4 w-4 mr-1" /> Add
              </>
            )}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Pack Detail Modal Component
export const PackDetailModal = ({ pack, isOpen, onClose, onAddToCart, adding }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  
  if (!pack) return null;
  
  const images = pack.images || [];
  const primaryIndex = images.findIndex(img => img.is_primary);
  const sortedImages = primaryIndex > 0 
    ? [images[primaryIndex], ...images.filter((_, i) => i !== primaryIndex)]
    : images;
  
  const currentImage = sortedImages[currentImageIndex]?.url;
  
  const nextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % sortedImages.length);
  };
  
  const prevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + sortedImages.length) % sortedImages.length);
  };
  
  const copyPackLink = async () => {
    const shareUrl = `${window.location.origin}/shop?pack=${pack.sku}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-4 md:inset-10 lg:inset-20 bg-zinc-900 border border-[#D4AF37]/30 rounded-lg z-50 overflow-hidden flex flex-col"
            data-testid="pack-detail-modal"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 hover:bg-[#D4AF37] text-white hover:text-black transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            
            {/* Share Button */}
            <button
              onClick={copyPackLink}
              className="absolute top-4 right-16 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 hover:bg-[#D4AF37]/20 text-white transition-colors"
            >
              {linkCopied ? <CheckCircle className="h-5 w-5 text-green-500" /> : <Share2 className="h-5 w-5" />}
            </button>
            
            <div className="flex-1 overflow-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
                {/* Image Section */}
                <div className="relative bg-black flex items-center justify-center p-8">
                  {sortedImages.length > 0 ? (
                    <>
                      <img 
                        src={currentImage} 
                        alt={pack.name}
                        className="max-w-full max-h-[60vh] object-contain"
                      />
                      
                      {/* Image Navigation */}
                      {sortedImages.length > 1 && (
                        <>
                          <button
                            onClick={prevImage}
                            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 hover:bg-[#D4AF37] text-white hover:text-black transition-colors"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <button
                            onClick={nextImage}
                            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 hover:bg-[#D4AF37] text-white hover:text-black transition-colors"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                          
                          {/* Thumbnails */}
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                            {sortedImages.map((img, idx) => (
                              <button
                                key={idx}
                                onClick={() => setCurrentImageIndex(idx)}
                                className={`w-12 h-12 rounded border-2 overflow-hidden transition-all ${
                                  idx === currentImageIndex ? 'border-[#D4AF37]' : 'border-transparent opacity-60 hover:opacity-100'
                                }`}
                              >
                                <img src={img.url} alt="" className="w-full h-full object-cover" />
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-64 flex items-center justify-center">
                      <Package className="h-24 w-24 text-[#D4AF37]/20" />
                    </div>
                  )}
                </div>
                
                {/* Details Section */}
                <div className="p-6 lg:p-8 overflow-auto">
                  {/* Header */}
                  <div className="mb-6">
                    {pack.is_featured && (
                      <span className="inline-flex items-center gap-1 text-xs bg-[#D4AF37] text-black px-2 py-1 rounded mb-2">
                        <Sparkles className="h-3 w-3" /> Featured Pack
                      </span>
                    )}
                    <h2 
                      className="text-3xl text-white mb-1"
                      style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}
                    >
                      {pack.name}
                    </h2>
                    <p className="text-sm text-zinc-500">{pack.sku}</p>
                  </div>
                  
                  {/* Pricing */}
                  <div className="bg-zinc-800/50 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-400">RRP Total:</span>
                      <span className="text-zinc-500 line-through">{formatPrice(pack.rrp_total)}</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-400">Pack Price:</span>
                      <span className="text-3xl font-bold text-[#D4AF37]">{formatPrice(pack.pack_price)}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-zinc-700">
                      <span className="text-green-500 font-medium">You Save:</span>
                      <span className="text-green-500 font-bold">{formatPrice(pack.savings)} ({pack.savings_percent}%)</span>
                    </div>
                  </div>
                  
                  {/* Description */}
                  {pack.description && (
                    <div className="mb-6">
                      <h3 className="text-sm text-zinc-500 uppercase tracking-wider mb-2">Description</h3>
                      <p className="text-zinc-300">{pack.description}</p>
                    </div>
                  )}
                  
                  {/* Cymbals Included */}
                  <div className="mb-6">
                    <h3 className="text-sm text-zinc-500 uppercase tracking-wider mb-3">
                      Cymbals Included ({pack.cymbals?.length || 0})
                    </h3>
                    <div className="space-y-2">
                      {pack.cymbals?.map((cymbal, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between bg-zinc-800/30 rounded p-3"
                        >
                          <div className="flex items-center gap-3">
                            <Disc3 className="h-5 w-5 text-[#D4AF37]" />
                            <div>
                              <p className="text-white font-medium">
                                {cymbal.size} {cymbal.type}
                              </p>
                              <p className="text-xs text-[#D4AF37]">{cymbal.series}</p>
                              {cymbal.notes && (
                                <p className="text-xs text-zinc-500">{cymbal.notes}</p>
                              )}
                            </div>
                          </div>
                          <span className="text-zinc-400 text-sm">{formatPrice(cymbal.individual_price)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Add to Cart */}
                  <Button
                    onClick={() => onAddToCart(pack)}
                    disabled={adding === pack.id}
                    className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black py-6 text-lg"
                    data-testid="modal-add-to-cart"
                  >
                    {adding === pack.id ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <ShoppingCart className="h-5 w-5 mr-2" />
                    )}
                    Add Pack to Cart
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Main Cymbal Packs Section Component
export default function CymbalPacksSection({ 
  featured = false, 
  limit = 10,
  showTitle = true,
  className = "",
  gridCols = "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
}) {
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [addingToCart, setAddingToCart] = useState(null);

  // Fetch packs
  useEffect(() => {
    const fetchPacks = async () => {
      try {
        setLoading(true);
        const url = featured 
          ? `${API}/cymbal-packs/?featured_only=true`
          : `${API}/cymbal-packs/`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setPacks(data.slice(0, limit));
        }
      } catch (err) {
        console.error("Failed to fetch packs:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPacks();
  }, [featured, limit]);

  // View pack details
  const handleViewDetails = (pack) => {
    setSelectedPack(pack);
    setModalOpen(true);
  };

  // Add pack to cart
  const handleAddToCart = (pack) => {
    setAddingToCart(pack.id);
    trackAddToCart({ id: pack.sku, name: pack.name, price: pack.pack_price, category: "Cymbal Pack" });
    
    // Get existing cart
    const cart = JSON.parse(localStorage.getItem("alchemist_cart") || "[]");
    
    // Create cart item for the pack
    const packItem = {
      sku: pack.sku,
      name: pack.name,
      type: "Cymbal Pack",
      series: pack.cymbals?.map(c => c.series).filter((v, i, a) => a.indexOf(v) === i).join(", "),
      price: pack.pack_price,
      quantity: 1,
      isPack: true,
      packId: pack.id,
      packContents: pack.cymbals?.map(c => `${c.size} ${c.type} (${c.series})`).join(", "),
      image: pack.images?.[0]?.url
    };
    
    // Check if pack already in cart
    const existingIndex = cart.findIndex(item => item.sku === pack.sku);
    if (existingIndex >= 0) {
      cart[existingIndex].quantity += 1;
    } else {
      cart.push(packItem);
    }
    
    localStorage.setItem("alchemist_cart", JSON.stringify(cart));
    
    // Dispatch event for cart counter updates
    window.dispatchEvent(new Event('cartUpdated'));
    window.dispatchEvent(new Event('storage'));
    
    setTimeout(() => {
      setAddingToCart(null);
      toast.success(`${pack.name} added to cart!`);
    }, 500);
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <Loader2 className="h-8 w-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (packs.length === 0) {
    return null; // Don't render section if no packs
  }

  return (
    <div className={className} data-testid="cymbal-packs-section">
      {showTitle && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 
              className="text-2xl md:text-3xl text-white"
              style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}
            >
              {featured ? "FEATURED CYMBAL PACKS" : "CYMBAL PACKS"}
            </h2>
            <p className="text-sm text-zinc-500">
              Curated bundles at special prices
            </p>
          </div>
          {featured && packs.length > 0 && (
            <Button
              variant="outline"
              onClick={() => navigate("/shop?view=packs")}
              className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              View All Packs
            </Button>
          )}
        </div>
      )}
      
      <div className={`grid ${gridCols} gap-6`}>
        {packs.map((pack) => (
          <PackCard
            key={pack.id}
            pack={pack}
            onViewDetails={handleViewDetails}
            onAddToCart={handleAddToCart}
            adding={addingToCart}
          />
        ))}
      </div>
      
      {/* Pack Detail Modal */}
      <PackDetailModal
        pack={selectedPack}
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedPack(null); }}
        onAddToCart={handleAddToCart}
        adding={addingToCart}
      />
    </div>
  );
}

// Featured Packs Carousel for Homepage
export function FeaturedPacksCarousel() {
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [addingToCart, setAddingToCart] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const containerRef = useState(null);

  useEffect(() => {
    const fetchPacks = async () => {
      try {
        const res = await fetch(`${API}/cymbal-packs/?featured_only=true`);
        if (res.ok) {
          const data = await res.json();
          setPacks(data.slice(0, 6));
        }
      } catch (err) {
        console.error("Failed to fetch featured packs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPacks();
  }, []);

  const handleAddToCart = (pack) => {
    setAddingToCart(pack.id);
    trackAddToCart({ id: pack.sku, name: pack.name, price: pack.pack_price, category: "Cymbal Pack" });
    const cart = JSON.parse(localStorage.getItem("alchemist_cart") || "[]");
    const packItem = {
      sku: pack.sku,
      name: pack.name,
      type: "Cymbal Pack",
      series: pack.cymbals?.map(c => c.series).filter((v, i, a) => a.indexOf(v) === i).join(", "),
      price: pack.pack_price,
      quantity: 1,
      isPack: true,
      packId: pack.id,
      packContents: pack.cymbals?.map(c => `${c.size} ${c.type} (${c.series})`).join(", "),
      image: pack.images?.[0]?.url
    };
    
    const existingIndex = cart.findIndex(item => item.sku === pack.sku);
    if (existingIndex >= 0) {
      cart[existingIndex].quantity += 1;
    } else {
      cart.push(packItem);
    }
    
    localStorage.setItem("alchemist_cart", JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    window.dispatchEvent(new Event('storage'));
    
    setTimeout(() => {
      setAddingToCart(null);
      toast.success(`${pack.name} added to cart!`);
    }, 500);
  };

  if (loading || packs.length === 0) {
    return null;
  }

  return (
    <section className="py-16 md:py-24 relative overflow-hidden" data-testid="featured-packs-carousel">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-900/50 to-black" />
      
      {/* Gold accent line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.3em] mb-2 block">
              Curated Collections
            </span>
            <h2 
              className="text-4xl md:text-5xl text-white mb-4"
              style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}
            >
              CYMBAL PACKS
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Hand-selected cymbal bundles at special prices. Perfect for upgrading your setup or starting fresh.
            </p>
          </motion.div>
        </div>
        
        {/* Packs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {packs.map((pack, index) => (
            <motion.div
              key={pack.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <PackCard
                pack={pack}
                onViewDetails={(p) => { setSelectedPack(p); setModalOpen(true); }}
                onAddToCart={handleAddToCart}
                adding={addingToCart}
              />
            </motion.div>
          ))}
        </div>
        
        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={() => navigate("/shop?view=packs")}
            className="bg-transparent border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-3"
          >
            View All Packs
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
      
      {/* Pack Detail Modal */}
      <PackDetailModal
        pack={selectedPack}
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedPack(null); }}
        onAddToCart={handleAddToCart}
        adding={addingToCart}
      />
    </section>
  );
}
